import { collectMiniProductHighlightCalculatedAttributes } from '../cms-blocks/MiniProductHighlight/collectCalculatedAttributes';
import { collectQuickAnswerCalculatedAttributes } from '../cms-blocks/QuickAnswer/collectCalculatedAttributes';
import { collectSimilarProductsCalculatedAttributes } from '../cms-blocks/SimilarProducts/collectCalculatedAttributes';
import { collectTableBuilderCalculatedAttributes } from '../cms-blocks/TableBuilder/collectCalculatedAttributes';
import { collectProductCardGridCalculatedAttributes } from '../components/ProductCardGrid/collectCalculatedAttributes';
import type { VerticalType } from '../types/cmsTypes';
import { TravelTripCriteria } from '../types/travelInsurance';
import { blockKey } from './blockKey';
import { type BlockVertical, NO_VERTICAL } from './moduleMode';
import {
  CalculatedAttributeProvider,
  getCalculatedAttributeProviderIn,
} from './registry';

/**
 * The collection side of the calculated-attributes engine (`resolve.ts` is the
 * consumption side): a module that RENDERS calculated attributes declares what
 * it draws, and the SSR collector walks the page, gathers the requests and
 * executes them per provider — the module never fetches anything itself, and
 * the collector never knows any module's shape.
 *
 * The division of labour is the point (CMS-1890 review, round 3). A collector
 * reports FACTS about its own instance: the products it draws, the attributes
 * it draws against them, the trip or the loan terms its editor authored, and —
 * where the block has a mode of its own — the vertical it is authored for. It
 * does not decide which of those attributes are calculated. That is one
 * judgement, it depends on the vertical, and it is made once, in `registry.ts`.
 *
 * Before, each collector made it separately and none of them could see a
 * vertical, so an `APR` row on a credit-card block armed a loan calculation and
 * the authored figure was replaced by a dash.
 *
 * A CMS component is serialized data crossing the server boundary, so it
 * cannot carry the declaration as a method. "This component type reports
 * calculated attributes" is therefore expressed as a typed entry in
 * `CALCULATED_ATTRIBUTES_COLLECTORS`, keyed by `__typename` — the registry is
 * the runtime-visible list of implementers, and each entry's function lives
 * next to its block, where the module's own semantics (modes, column types,
 * visibility) belong.
 */

/** The loan fields as authored on a module. Each field stands alone. */
export type LoanFields = { amount?: number; tenure?: number };

/**
 * What a PL module asks the calculator for — the input of the batch
 * `calculatePersonalLoans` call. Unlike TI (where the quote covers the whole
 * market), the PL batch is scoped by products, so they are part of the
 * request; the executor unions `productIds` across modules with equal terms
 * into one call.
 *
 * Facts about the module, nothing more: what its loan fields MEAN — a fixed
 * illustration or a starting value, whose scope it joins, whether the reader's
 * filter reaches it — is decided by the host's loan-scope engine, which sees
 * the whole page. A collector sees only its own component (CMS-1890).
 */
export type PersonalLoanCalculationCriteria = {
  /** The products the module renders — the batch call's scope. */
  productIds: string[];
  /** The loan fields authored on this instance, if the block has any. */
  loanFields?: LoanFields;
};

/** A TI module's request: quote the authored trip. */
export type TravelQuoteCalculationRequest = {
  provider: 'TI_QUOTE';
  moduleId: string;
  criteria: TravelTripCriteria;
};

/** A PL module's request: run the calculator over its products and terms. */
export type PersonalLoanCalculationRequest = {
  provider: 'PL_CALCULATOR';
  moduleId: string;
  criteria: PersonalLoanCalculationCriteria;
};

/**
 * One module instance's request for calculated attributes, tagged by the
 * provider that must serve it (mirrors `CalculationSources`). `moduleId` keys
 * the result back to the requesting instance — two modules on one page may
 * request different criteria from the same provider.
 */
export type CalculatedAttributesRequest =
  | TravelQuoteCalculationRequest
  | PersonalLoanCalculationRequest;

/**
 * One group of what a block draws: the attributes shown against these
 * products.
 *
 * Most blocks fill a single group, because their attribute set is authored for
 * the whole module. Product Card Grid fills one per card: there the
 * highlighted attribute is authored card by card, so "which products need a
 * figure" is answered card by card too. The grouping is what lets the grid
 * report that without judging anything itself — the engine keeps the cards
 * whose attribute is calculated and drops the rest.
 */
export type CollectedAttributeGroup = {
  /** Product codes, as `CmsProduct.title` holds them. */
  productIds: string[];
  /** The attribute names drawn against those products, exactly as authored. */
  attributeNames: (string | undefined | null)[];
};

/**
 * What a collector returns: the block's own facts, without its address and
 * without a verdict. The dispatcher keys it by `blockKey` — a collector sees
 * only its own component and has no business choosing how the page addresses
 * blocks.
 */
export type CollectedRequest = {
  /**
   * The vertical the block itself is authored for, when it knows one. Table
   * Builder and Quick Answer do — their `mode` says it, deliberately
   * independently of the page — which is why such a block keeps working on an
   * article or a credit-card page; in General they state `NO_VERTICAL`. A
   * block with no mode of its own leaves this out and the page's vertical
   * stands in.
   */
  vertical?: BlockVertical;
  /** What the block draws. */
  groups: CollectedAttributeGroup[];
  /** The trip the block is authored for, if it has one. */
  trip?: TravelTripCriteria;
  /** The loan terms authored on the block, if it has any. */
  loanFields?: LoanFields;
};

/**
 * A module's implementation of the collection contract: given ITS mapper-typed
 * component, report the instance's facts, or `undefined` when the instance
 * reports nothing at all (hidden, or in a mode that renders authored values
 * only).
 */
export type CalculatedAttributesCollector<C> = (
  component: C,
) => CollectedRequest | undefined;

/**
 * A request plus where its module sits in the flattened component list. The
 * position is attached by the collection pass, not by the collector: a module
 * cannot see the page. It lets the resolver pick a neighbour by position — the
 * carrier is the first on the page today, and "the nearest one above" is a
 * change to that one policy rather than to this contract.
 */
export type PositionedCalculatedAttributesRequest =
  CalculatedAttributesRequest & { position: number };

/**
 * The implementers, keyed by the component's `__typename`. Adding a module to
 * the engine = one entry here plus its collector next to the block. The
 * `satisfies` ties each key to a collector (any narrower component type is
 * fine — dispatch by `__typename` guarantees it receives its own).
 */
const CALCULATED_ATTRIBUTES_COLLECTORS = {
  ComponentPagesComponentTableBuilderComponent:
    collectTableBuilderCalculatedAttributes,
  ComponentPagesComponentQuickAnswerComponent:
    collectQuickAnswerCalculatedAttributes,
  ComponentPagesComponentProductCardGridComponent:
    collectProductCardGridCalculatedAttributes,
  ComponentPagesComponentSimilarProducts:
    collectSimilarProductsCalculatedAttributes,
  ComponentPagesComponentMiniProductHighlightComponent:
    collectMiniProductHighlightCalculatedAttributes,
} satisfies Record<string, CalculatedAttributesCollector<never>>;

/**
 * The page a component is being collected on. Only the vertical is needed, and
 * only to stand in for the blocks that state none of their own. Deliberately
 * not called a context — that word is React's here, and this is a plain
 * argument.
 */
export type CollectionPage = { vertical?: VerticalType | null };

/**
 * What the component's collector reports, or nothing for an unregistered type.
 * The cast is sound because the key and the argument come from the same
 * object, so a collector only ever receives the component type it was
 * registered under.
 */
const collect = (
  component: { __typename?: string } | undefined | null,
): CollectedRequest | undefined => {
  const typename = component?.__typename;
  if (!typename) return undefined;
  const collector = (
    CALCULATED_ATTRIBUTES_COLLECTORS as Partial<
      Record<string, CalculatedAttributesCollector<unknown>>
    >
  )[typename];
  return collector?.(component);
};

const blockVertical = (
  collected: CollectedRequest,
  page: CollectionPage | undefined,
): BlockVertical | undefined => collected.vertical ?? page?.vertical ?? undefined;

/**
 * The request of one collected block, or `undefined` when nothing it draws is
 * calculated in its vertical. Every attribute the block reported is judged
 * against that one vertical, and one vertical admits one provider, so a block
 * ends up asking one provider or none.
 */
const requestOf = (
  moduleId: string,
  collected: CollectedRequest,
  vertical: VerticalType | undefined,
): CalculatedAttributesRequest | undefined => {
  let provider: CalculatedAttributeProvider | undefined;
  const productIds: string[] = [];
  for (const group of collected.groups) {
    const groupProvider = group.attributeNames
      .map((name) => getCalculatedAttributeProviderIn(name, vertical))
      .find((candidate) => candidate !== undefined);
    if (!groupProvider) continue;
    provider = groupProvider;
    productIds.push(...group.productIds);
  }
  if (!provider) return undefined;

  if (provider === 'TI_QUOTE') {
    // The quote covers the market, so the products are not part of it — they
    // are matched to quotes at render, by `INSUREMO_PRODUCT_UID`. Without an
    // authored trip there is nothing to quote.
    return collected.trip
      ? { provider, moduleId, criteria: collected.trip }
      : undefined;
  }

  // The batch is scoped by products, so a block that draws none has nothing to
  // ask for: a table of category rows, a grid whose cards carry no product.
  const scope = Array.from(new Set(productIds));
  if (!scope.length) return undefined;

  return {
    provider,
    moduleId,
    criteria: { productIds: scope, loanFields: collected.loanFields },
  };
};

/** What the engine decided for one surface — a block, or anything that states its own facts. */
export type CollectedSurface = {
  /** How the page addresses it. */
  moduleId: string;
  /** The vertical the engine runs it under — its CTA's too (CMS-2052). */
  vertical: BlockVertical | undefined;
  /**
   * The vertical is the surface's own statement, not the page's. Its
   * attributes in that vertical are then calculated even with no figures to
   * show — a dash, never the catalogue's value (CMS-1928 R2).
   */
  statesVertical: boolean;
  /** What it asks a provider for, if anything. */
  request?: CalculatedAttributesRequest;
  /** The trip it is authored for, if it has one. */
  trip?: TravelTripCriteria;
};

/**
 * The engine's core: a surface's facts in, its decision out — the vertical it
 * runs under and the request it makes. For a surface that is not a CMS block
 * (the apply form, the product schema): it states the facts a collector would.
 * Blocks come in through `collectPage`, which finds their collectors.
 */
export const collectSurface = (
  facts: CollectedRequest & { moduleId: string },
  page?: CollectionPage,
): CollectedSurface => {
  const vertical = blockVertical(facts, page);
  return {
    moduleId: facts.moduleId,
    vertical,
    statesVertical: facts.vertical !== undefined,
    request: requestOf(
      facts.moduleId,
      facts,
      vertical === NO_VERTICAL ? undefined : vertical,
    ),
    trip: facts.trip,
  };
};

/** One block as the engine sees it after collection. */
export type CollectedBlock = Omit<CollectedSurface, 'request'> & {
  request?: PositionedCalculatedAttributesRequest;
};

/**
 * The block layer over the core: every block of the page the engine knows,
 * each collected once by its own collector and judged by `collectSurface`.
 * Blocks of a type it does not know are left out — their vertical is wherever
 * they keep it themselves.
 *
 * The page argument is optional so a story or a unit test can collect without
 * one. A block that states its own vertical then answers exactly as it does on
 * a page; a block that inherits reports nothing, which is the honest answer
 * when there is nothing to inherit from.
 */
export const collectPage = (
  components:
    | readonly ({ __typename?: string; id?: string } | undefined | null)[]
    | undefined,
  page?: CollectionPage,
): CollectedBlock[] =>
  (components ?? []).flatMap((component, position) => {
    const collected = collect(component);
    if (!component || !collected) return [];
    // Keyed the way the page addresses blocks, not by the bare Strapi id,
    // which is unique only within a type.
    const { request, ...surface } = collectSurface(
      { ...collected, moduleId: blockKey(component) },
      page,
    );
    return [{ ...surface, request: request && { ...request, position } }];
  });
